import { log } from '@/log'
import { ApiProductclass } from '@/models/api/productclass'
import {
  availableSameDayShipping,
  SameDayShippingAvailability,
  unknownSameDayShipping,
} from '@/models/item/sku/same-day-shipping'

export interface CartProductDetail {
  salesStartAt: string | null
  salesEndAt: string | null
  isPreSale: boolean
  maxSellCount: number | null
  stock: number | null
  colorLabel: string | null
  colorName: string | null
  thumbnail: string | null
  root: Record<string, unknown>
  stockAppealText: string | null
  canRequestRestock: boolean | null
  sameDayShipping: SameDayShippingAvailability
}

/**
 * server の `productDetail.sameDayShipping` を内部表現へ変換する。
 * 入力の形は `{status: 'available', orderDeadlineAt: ISO8601} | {status: 'unknown'}`。
 * 形が想定と違う（欠落・未知の status・日時として読めない）ものは、当日発送を約束できないので不明に寄せる。
 */
function parseSameDayShipping(raw: unknown): SameDayShippingAvailability {
  if (!raw || typeof raw !== 'object') {
    return unknownSameDayShipping
  }

  const { status, orderDeadlineAt } = raw as { status?: unknown, orderDeadlineAt?: unknown }
  if (status !== 'available') {
    return unknownSameDayShipping
  }

  const deadline = typeof orderDeadlineAt === 'string' ? new Date(orderDeadlineAt) : null
  if (deadline === null || isNaN(deadline.getTime())) {
    log.warn('sameDayShipping is available but orderDeadlineAt is not a valid datetime, treating as unknown', { raw })

    return unknownSameDayShipping
  }

  return availableSameDayShipping(deadline)
}

export class ProductDetail {
  /**
   * 空のProductDetailインスタンスを返す
   * @see src/ReadModel/Cart/ProductDetail/ProductDetail::none()
   */
  static empty(): ProductDetail {
    return new ProductDetail({
      salesStartAt: null,
      salesEndAt: null,
      isPreSale: false,
      maxSellCount: null,
      stock: null,
      colorLabel: null,
      colorName: null,
      thumbnail: null,
      root: {},
      stockAppealText: null,
      canRequestRestock: null,
      sameDayShipping: unknownSameDayShipping,
    })
  }

  /**
   * データを安全にパースしてProductDetailインスタンスを返す
   * falsyな値やパースエラー時にはemptyにフォールバックする
   * @param data - パース対象のデータ
   * @returns ProductDetailインスタンス（パース失敗時はempty）
   *
   * NOTE: 現時点では ProductDetail のコンストラクタ内で ApiProductclass を生成するだけで、
   * 通常のケースではエラーが発生する可能性は低い。しかし、以下のようなケースを想定して
   * 防御的プログラミングとして try-catch を設けている:
   * - 将来的に ApiProductclass や Resource のコンストラクタでバリデーションやエラーが追加された場合
   * - システムレベルのエラー（メモリ不足など）
   * - 予期しないデータ構造による問題
   */
  static safeParse(data: any): ProductDetail {
    if (!data) {
      return ProductDetail.empty()
    }

    try {
      return new ProductDetail({
        salesStartAt: data.salesStartAt != null ? data.salesStartAt : null,
        salesEndAt: data.salesEndAt != null ? data.salesEndAt : null,
        isPreSale: data.isPreSale != null ? data.isPreSale : false,
        maxSellCount: data.maxSellCount != null ? data.maxSellCount : null,
        stock: data.stock != null ? data.stock : null,
        colorLabel: data.colorLabel != null ? data.colorLabel : null,
        colorName: data.colorName != null ? data.colorName : null,
        thumbnail: data.thumbnail != null ? data.thumbnail : null,
        root: data.root != null ? data.root : {},
        stockAppealText: data.stockAppealText != null ? data.stockAppealText : null,
        canRequestRestock: data.canRequestRestock != null ? data.canRequestRestock : null,
        sameDayShipping: parseSameDayShipping(data.sameDayShipping),
      })
    } catch (error) {
      log.warn('Failed to parse ProductDetail, falling back to empty', { error, data })

      return ProductDetail.empty()
    }
  }

  private readonly rootProductclass: ApiProductclass

  constructor(private readonly data: CartProductDetail) {
    // 元の実装では new ApiProductclass(undefined) も使われていたので、その挙動を維持
    this.rootProductclass = new ApiProductclass(data.root || undefined)
  }

  get salesStartAt(): string | null {
    return this.data.salesStartAt
  }

  get salesEndAt(): string | null {
    return this.data.salesEndAt
  }

  get isPreSale(): boolean {
    return this.data.isPreSale
  }

  get maxSellCount(): number | null {
    return this.data.maxSellCount
  }

  get stock(): number | null {
    return this.data.stock
  }

  get stockAppealText(): string | null {
    return this.data.stockAppealText
  }

  /** null は SKU 情報が取得できない(非公開など)ことを表す */
  get canRequestRestock(): boolean | null {
    return this.data.canRequestRestock
  }

  get colorLabel(): string | null {
    return this.data.colorLabel
  }

  get colorName(): string | null {
    return this.data.colorName
  }

  get thumbnail(): string | null {
    return this.data.thumbnail
  }

  get sameDayShipping(): SameDayShippingAvailability {
    return this.data.sameDayShipping
  }

  /**
   * FKU情報が取得できたかどうかを判定する。
   * FKUが取得できた場合のみcolorLabelまたはcolorNameが設定される。
   * @see src/ReadModel/Cart/ProductDetail/ProductDetailFactory.php
   */
  get hasFku(): boolean {
    return this.data.colorLabel !== null || this.data.colorName !== null
  }

  get hasRoot(): boolean {
    return this.data.root && Object.keys(this.data.root).length > 0
  }

  getRootProductName(defaultValue: string): string {
    return this.hasRoot ? this.rootProductclass.productName : defaultValue
  }

  get root(): ApiProductclass | null {
    return this.hasRoot ? this.rootProductclass : null
  }

  hasRootTag(pathname: string): boolean {
    return this.hasRoot && this.rootProductclass.hasTag(pathname)
  }

  get isNovelty(): boolean {
    return this.hasRoot ? this.rootProductclass.isNovelty : false
  }

  /**
   * 販売開始日時を取得する。ProductDetailに情報がない場合はデフォルト値を返す。
   * ProductDetailの内部データ型を外部に秘匿するため、このメソッドを使用する。
   * @param defaultValue - ProductDetailに情報がない場合に返すデフォルト値
   * @returns 販売開始日時（文字列）
   */
  getSalesStartAtOrElse(defaultValue: string): string {
    return this.data.salesStartAt != null ? this.data.salesStartAt : defaultValue
  }

  /**
   * 販売終了日時を取得する。ProductDetailに情報がない場合はデフォルト値を返す。
   * ProductDetailの内部データ型を外部に秘匿するため、このメソッドを使用する。
   * @param defaultValue - ProductDetailに情報がない場合に返すデフォルト値
   * @returns 販売終了日時（文字列）
   */
  getSalesEndAtOrElse(defaultValue: string): string {
    return this.data.salesEndAt != null ? this.data.salesEndAt : defaultValue
  }
}
